import { ENV_CONFIG } from '../config/env.js';

// Perplexity Sonar API Client
class PerplexityClient {
  constructor() {
    this.apiKey = ENV_CONFIG.PERPLEXITY_API_KEY;
    this.baseUrl = ENV_CONFIG.PERPLEXITY_BASE_URL;
  }

  async makeRequest(payload) {
    if (this.apiKey === 'INSERT_YOUR_API_KEY_HERE') {
      console.warn('Perplexity API key not configured. Using mock response.');
      return this.mockResponse(payload);
    }

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Perplexity API error:', error);
      return this.mockResponse(payload);
    }
  }

  mockResponse(payload) {
    // Simula una respuesta completa con citations y fuentes
    const mockResponses = {
      nutrition: `## What is Nutrition?

Nutrition is the study of how food and beverages affect your body, focusing on the essential nutrients necessary for health, growth, and disease prevention[1][2].

## Key Nutrients in Food

There are six main classes of nutrients your body needs[3][4]:

- **Carbohydrates**: Provide energy for your body and are found in foods like grains, fruits, and vegetables
- **Proteins**: Essential for building and repairing tissues, found in meats, beans, dairy, and some grains
- **Fats**: Important for brain health, energy, and absorption of certain vitamins. Healthy fats are found in fish, nuts, seeds, and plant oils
- **Vitamins**: Organic compounds needed for many biochemical processes, such as vitamin C (immune health), vitamin D (bone health), and more
- **Minerals**: Include calcium and iron, which support bone health, blood health, and cellular functions
- **Water**: Vital for all bodily processes, including digestion, nutrient transport, and temperature regulation[4]

## Nutritional Guidelines and Healthy Eating

To promote health and reduce chronic disease risk, nutrition experts recommend a balanced eating pattern that includes[2][5]:

- Fruits and vegetables
- Whole grains
- Lean proteins
- Low-fat dairy
- Healthy fats (unsaturated)
- Limited added sugars, salt, and saturated fat

## Nutrition Across the Lifespan

Nutrient needs vary based on age, life stage, and health conditions. For example, older adults may require more calcium and vitamin D for bone health, while children need adequate nutrients to support growth and development[3][6].

## The Role of Nutrition in Disease Prevention and Management

Healthy nutrition helps prevent or manage chronic health conditions like:

- Heart disease (through heart-healthy diets)
- Diabetes (managing carbohydrates and blood sugar)
- Hypertension (limiting sodium, following the DASH diet)
- Osteoporosis (adequate calcium and vitamin D)
- Cancer, digestive disorders, and more[6]

---

**Summary:** Nutrition is the foundation of health, focusing on how the body uses food to function, grow, and prevent disease. Consuming a varied, balanced diet and adapting nutrition advice to individual needs is essential for long-term health and well-being[2][5][7].`,
      
      medication: `## Medication Information

Medications are substances used to treat, prevent, or diagnose diseases[1]. It's essential to understand how they work and their possible side effects.

### Types of Medications

- **Prescription medications**: Require a doctor's prescription for dispensing
- **Over-the-counter medications**: Available without a prescription
- **Generic medications**: Equivalent versions of brand-name drugs[2]

### Medication Safety

Always consult with your doctor or pharmacist before:
- Starting a new medication
- Combining medications
- Changing doses[3]

### Drug Interactions

Medications can interact with each other or with foods, which can be dangerous[4][5].`,

      default: `## General Health Information

As your health assistant, I'm here to provide reliable information based on scientific evidence[1].

### Preventive Health Principles

Prevention is the best medicine. Maintaining healthy habits can prevent many chronic diseases[2].

- Balanced nutrition
- Regular exercise
- Adequate sleep
- Stress management[3]

### When to Seek Medical Attention

It's important to know when to contact a healthcare professional[4][5].`,
    };

    const content = payload.messages[0]?.content?.toLowerCase() || '';
    let response;
    
    if (content.includes('nutrición') || content.includes('nutrition') || content.includes('comida') || content.includes('dieta')) {
      response = mockResponses.nutrition;
    } else if (content.includes('medicamento') || content.includes('medicine') || content.includes('pill') || content.includes('droga')) {
      response = mockResponses.medication;
    } else {
      response = mockResponses.default;
    }

    // Simular estructura de respuesta de Perplexity con citations
    return Promise.resolve({
      choices: [{
        message: {
          content: response,
          role: 'assistant'
        }
      }],
      citations: [
        {
          id: 1,
          title: "Mayo Clinic - Health Information",
          url: "https://www.mayoclinic.org/",
          snippet: "Reliable medical information verified by experts"
        },
        {
          id: 2,
          title: "MedlinePlus",
          url: "https://medlineplus.gov/",
          snippet: "U.S. National Library of Medicine"
        },
        {
          id: 3,
          title: "CDC - Centers for Disease Control",
          url: "https://www.cdc.gov/",
          snippet: "Official public health guidelines"
        },
        {
          id: 4,
          title: "NIH - National Institutes of Health",
          url: "https://www.nih.gov/",
          snippet: "Medical research and clinical guidelines"
        },
        {
          id: 5,
          title: "American Heart Association",
          url: "https://www.heart.org/",
          snippet: "Cardiovascular health information"
        },
        {
          id: 6,
          title: "Nutrition.gov",
          url: "https://www.nutrition.gov/",
          snippet: "Official government nutrition resources"
        },
        {
          id: 7,
          title: "FDA - Food and Drug Administration",
          url: "https://www.fda.gov/",
          snippet: "Official information about medications and food"
        }
      ],
      usage: {
        prompt_tokens: payload.messages[0]?.content?.length || 0,
        completion_tokens: response.length,
        total_tokens: (payload.messages[0]?.content?.length || 0) + response.length
      }
    });
  }
}

// Agent Types and Routing System
export class HealthAgentRouter {
  constructor() {
    this.client = new PerplexityClient();
    this.agentHistory = [];
  }

  // Main routing logic
  async routeToAgent(userInput, context = {}) {
    const intent = this.classifyIntent(userInput);
    const agent = this.selectAgent(intent, context);
    
    this.agentHistory.push({
      timestamp: new Date(),
      intent,
      agent: agent.name,
      input: userInput
    });

    return await agent.process(userInput, context);
  }

  // Intent classification
  classifyIntent(input) {
    const text = input.toLowerCase();
    
    // Emergency detection
    if (this.isEmergency(text)) {
      return { type: 'emergency', priority: 'critical', confidence: 0.95 };
    }
    
    // Medical consultation
    if (text.match(/\b(medication|pill|dose|side effect|prescription)\b/)) {
      return { type: 'medication', priority: 'high', confidence: 0.85 };
    }
    
    // Vital signs and monitoring
    if (text.match(/\b(blood pressure|heart rate|temperature|weight|glucose|vitals?)\b/)) {
      return { type: 'vitals', priority: 'medium', confidence: 0.80 };
    }
    
    // Nutrition and diet
    if (text.match(/\b(food|diet|nutrition|meal|calories|carbs|protein)\b/)) {
      return { type: 'nutrition', priority: 'medium', confidence: 0.75 };
    }
    
    // Mental health and mood
    if (text.match(/\b(feel|mood|anxiety|depression|stress|worry|sad|happy)\b/)) {
      return { type: 'mental_health', priority: 'medium', confidence: 0.70 };
    }
    
    // General health inquiry
    if (text.match(/\b(health|symptoms?|condition|doctor|medical)\b/)) {
      return { type: 'general_health', priority: 'low', confidence: 0.65 };
    }
    
    // Default to general conversation
    return { type: 'general', priority: 'low', confidence: 0.50 };
  }

  isEmergency(text) {
    const emergencyKeywords = [
      'emergency', 'urgent', 'help', 'pain', 'chest pain', 'can\'t breathe',
      'difficulty breathing', 'heart attack', 'stroke', 'unconscious',
      'bleeding', 'severe', 'call 911', 'ambulance'
    ];
    
    return emergencyKeywords.some(keyword => text.includes(keyword));
  }

  // Agent selection based on intent
  selectAgent(intent, context) {
    switch (intent.type) {
      case 'emergency':
        return new EmergencyAgent(this.client);
      case 'medication':
        return new MedicationAgent(this.client);
      case 'vitals':
        return new VitalsMonitoringAgent(this.client);
      case 'nutrition':
        return new NutritionAgent(this.client);
      case 'mental_health':
        return new MentalHealthAgent(this.client);
      case 'general_health':
        return new HealthResearchAgent(this.client);
      default:
        return new MainHealthAgent(this.client);
    }
  }
}

// Base Agent Class
class BaseAgent {
  constructor(client, name, model = ENV_CONFIG.MAIN_AGENT_MODEL) {
    this.client = client;
    this.name = name;
    this.model = model;
    this.domainFilter = [];
    this.contextSize = ENV_CONFIG.DEFAULT_SEARCH_CONTEXT_SIZE;
  }

  async process(input, context = {}) {
    const systemPrompt = this.getSystemPrompt();
    const payload = this.buildPayload(input, systemPrompt, context);
    
    try {
      const response = await this.client.makeRequest(payload);
      return this.formatResponse(response, context);
    } catch (error) {
      console.error(`Error in ${this.name}:`, error);
      return this.handleError(error);
    }
  }

  buildPayload(input, systemPrompt, context) {
    const payload = {
      model: this.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: input }
      ],
      max_tokens: ENV_CONFIG.DEFAULT_MAX_TOKENS,
      web_search_options: {
        search_context_size: this.contextSize
      }
    };

    // Add domain filtering if enabled
    if (ENV_CONFIG.ENABLE_DOMAIN_FILTERING && this.domainFilter.length > 0) {
      payload.search_domain_filter = this.domainFilter;
    }

    // Add date filtering for recent information
    if (context.needsRecentInfo) {
      payload.search_recency_filter = 'month';
    }

    return payload;
  }

  formatResponse(response, context) {
    const canEscalate = this.canEscalate(response.choices[0].message.content);
    const nextAgent = this.suggestNextAgent(response.choices[0].message.content);
    
    return {
      content: response.choices[0].message.content,
      agent: this.name,
      model: this.model,
      timestamp: new Date(),
      canEscalate,
      nextSuggestedAgent: nextAgent,
      apiResponse: response, // Include full API response for citations
      context: {
        confidence: context.confidence || 'high',
        priority: context.priority || 'normal',
        domainFiltered: context.domainFiltered || false,
        searchContextSize: context.searchContextSize || 'medium'
      }
    };
  }

  canEscalate(content) {
    const escalationTriggers = [
      'consult with a doctor',
      'seek immediate medical attention',
      'emergency',
      'serious condition',
      'beyond my expertise'
    ];
    
    return escalationTriggers.some(trigger => 
      content.toLowerCase().includes(trigger)
    );
  }

  suggestNextAgent(content) {
    // Override in subclasses for specific routing logic
    return null;
  }

  handleError(error) {
    return {
      content: `I'm sorry, I encountered an issue processing your request. Please try again or contact your healthcare provider if this is urgent.`,
      agent: this.name,
      error: true,
      timestamp: new Date()
    };
  }

  getSystemPrompt() {
    return "You are a helpful healthcare AI assistant.";
  }
}

// Main Health Agent (Primary Router)
class MainHealthAgent extends BaseAgent {
  constructor(client) {
    super(client, 'MainHealthAgent', ENV_CONFIG.MAIN_AGENT_MODEL);
    this.domainFilter = ENV_CONFIG.MEDICAL_DOMAINS;
  }

  getSystemPrompt() {
    return `You are Sophira, a compassionate AI health companion for elderly patients. 
    
Your role is to:
- Provide general health guidance and support
- Help with medication reminders and basic health questions
- Monitor vital signs and symptoms
- Offer emotional support and companionship
- Escalate to appropriate specialists when needed
- Always prioritize patient safety and encourage professional medical consultation for serious concerns

Guidelines:
- Be warm, empathetic, and use simple language
- Focus on health maintenance and prevention
- Encourage healthy lifestyle choices
- Always recommend consulting healthcare providers for medical decisions
- Provide accurate, evidence-based information from trusted medical sources`;
  }

  suggestNextAgent(content) {
    if (content.toLowerCase().includes('medication')) {
      return 'MedicationAgent';
    } else if (content.toLowerCase().includes('nutrition')) {
      return 'NutritionAgent';
    } else if (content.toLowerCase().includes('vital')) {
      return 'VitalsMonitoringAgent';
    }
    return null;
  }
}

// Specialized Agents
class EmergencyAgent extends BaseAgent {
  constructor(client) {
    super(client, 'EmergencyAgent', ENV_CONFIG.SPECIALIST_AGENT_MODEL);
    this.domainFilter = ENV_CONFIG.EMERGENCY_DOMAINS;
    this.contextSize = 'high';
  }

  getSystemPrompt() {
    return `You are an emergency health AI assistant. Your primary role is to:
    
1. Assess the urgency of health situations
2. Provide immediate first aid guidance
3. Direct users to appropriate emergency services
4. Remain calm and provide clear, actionable instructions

CRITICAL: Always advise calling emergency services (911) for:
- Chest pain or heart attack symptoms
- Difficulty breathing or choking
- Severe bleeding or trauma
- Loss of consciousness
- Stroke symptoms (FAST test)
- Severe allergic reactions
- Suspected overdose or poisoning

Provide step-by-step emergency instructions while emergency services are en route.`;
  }
}

class MedicationAgent extends BaseAgent {
  constructor(client) {
    super(client, 'MedicationAgent', ENV_CONFIG.SPECIALIST_AGENT_MODEL);
    this.domainFilter = ENV_CONFIG.MEDICATION_DOMAINS;
  }

  getSystemPrompt() {
    return `You are a medication management AI assistant specializing in:
    
- Medication information and interactions
- Dosage guidelines and scheduling
- Side effects and contraindications
- Drug safety and proper administration
- Medication adherence strategies

Guidelines:
- Always recommend consulting pharmacists or doctors for medication changes
- Provide accurate drug information from FDA and medical databases
- Help with medication organization and reminders
- Identify potential drug interactions
- Never recommend stopping or changing prescribed medications without medical supervision`;
  }
}

class VitalsMonitoringAgent extends BaseAgent {
  constructor(client) {
    super(client, 'VitalsMonitoringAgent', ENV_CONFIG.MAIN_AGENT_MODEL);
    this.domainFilter = ENV_CONFIG.MEDICAL_DOMAINS;
  }

  getSystemPrompt() {
    return `You are a vital signs monitoring AI assistant focusing on:
    
- Blood pressure interpretation and trends
- Heart rate analysis
- Blood glucose monitoring for diabetics
- Weight management tracking
- Temperature and respiratory monitoring

Capabilities:
- Explain normal vs. abnormal ranges
- Identify concerning trends requiring medical attention
- Provide guidance on proper measurement techniques
- Suggest when to contact healthcare providers
- Help with tracking and logging vital signs`;
  }
}

class NutritionAgent extends BaseAgent {
  constructor(client) {
    super(client, 'NutritionAgent', ENV_CONFIG.MAIN_AGENT_MODEL);
    this.domainFilter = ENV_CONFIG.NUTRITION_DOMAINS;
  }

  getSystemPrompt() {
    return `You are a nutrition AI assistant specializing in elderly health and chronic condition management:
    
Focus areas:
- Heart-healthy diets for cardiovascular conditions
- Diabetic meal planning and carbohydrate counting
- Medication-food interactions
- Hydration and nutrition for aging adults
- Safe food preparation and storage

Guidelines:
- Provide evidence-based nutrition advice from USDA and medical sources
- Consider common elderly dietary restrictions and preferences
- Emphasize food safety for immunocompromised individuals
- Recommend consulting dietitians for personalized meal plans
- Account for medication timing and food interactions`;
  }
}

class MentalHealthAgent extends BaseAgent {
  constructor(client) {
    super(client, 'MentalHealthAgent', ENV_CONFIG.SPECIALIST_AGENT_MODEL);
    this.domainFilter = ['nih.gov', 'cdc.gov', 'samhsa.gov', 'nami.org'];
  }

  getSystemPrompt() {
    return `You are a mental health support AI assistant for elderly patients:
    
Your role includes:
- Providing emotional support and active listening
- Helping with mood tracking and emotional wellness
- Offering coping strategies for anxiety and depression
- Recognizing signs of serious mental health concerns
- Connecting users with appropriate mental health resources

Guidelines:
- Be empathetic, non-judgmental, and supportive
- Recognize when professional intervention is needed
- Provide crisis resources for emergency situations
- Encourage social connection and engagement
- Support medication adherence for mental health conditions
- Never provide therapy or diagnose mental health conditions`;
  }
}

class HealthResearchAgent extends BaseAgent {
  constructor(client) {
    super(client, 'HealthResearchAgent', ENV_CONFIG.RESEARCH_AGENT_MODEL);
    this.domainFilter = ENV_CONFIG.MEDICAL_DOMAINS;
    this.contextSize = 'high';
  }

  getSystemPrompt() {
    return `You are a health research AI assistant providing comprehensive medical information:
    
Capabilities:
- Research current medical guidelines and treatments
- Explain medical conditions in understandable terms
- Provide evidence-based health information
- Summarize recent medical research and developments
- Help understand test results and medical terminology

Standards:
- Use only peer-reviewed and authoritative medical sources
- Present information clearly for elderly patients
- Always emphasize the importance of professional medical consultation
- Avoid providing specific medical advice or diagnoses
- Focus on education and health literacy improvement`;
  }
}

// Export the main router for use in the React app
export default HealthAgentRouter; 